import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileUp, Link as LinkIcon, FileText, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

export default function FilesLinksTab({ projectId, files, driveLinks, onChanged }) {
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveForm, setDriveForm] = useState({ url: "", title: "" });

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fd2 = new FormData();
      fd2.append("file_id", r.data.id);
      await api.post(`/projects/${projectId}/files`, fd2, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached");
      onChanged?.();
    } catch {
      toast.error("Upload failed");
    }
  };

  const addDriveLink = async () => {
    try {
      await api.post(`/projects/${projectId}/drive-links`, driveForm);
      toast.success("Link added");
      setDriveOpen(false); setDriveForm({ url: "", title: "" });
      onChanged?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="gc-label">Uploaded Files</div>
          <label className="cursor-pointer text-xs font-medium text-[#0a2540] inline-flex items-center gap-1.5">
            <FileUp size={14} /> Upload
            <input type="file" className="hidden" onChange={uploadFile} data-testid="input-upload-file" />
          </label>
        </div>
        <div className="space-y-1.5">
          {(files || []).length === 0 && <div className="text-xs text-slate-400">No files</div>}
          {(files || []).map((f) => (
            <a key={f.file_id} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}/download`} target="_blank" rel="noreferrer"
               className="flex items-center justify-between px-3 py-2 border border-slate-200 hover:border-[#0a2540]" data-testid={`file-${f.file_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={14} className="text-slate-400" />
                <div className="text-xs truncate">{f.filename}</div>
              </div>
              <span className="text-[10px] text-slate-400 font-mono-data">{(f.size / 1024).toFixed(1)} KB</span>
            </a>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="gc-label">Google Drive Links</div>
          <Dialog open={driveOpen} onOpenChange={setDriveOpen}>
            <DialogTrigger asChild>
              <button className="text-xs font-medium text-[#0a2540] inline-flex items-center gap-1.5" data-testid="btn-add-drive">
                <LinkIcon size={14} /> Add link
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Add Google Drive Link</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input className="mt-1 rounded-sm" value={driveForm.title} onChange={(e) => setDriveForm({ ...driveForm, title: e.target.value })} data-testid="input-drive-title" />
                </div>
                <div>
                  <Label className="text-xs">Drive URL *</Label>
                  <Input className="mt-1 rounded-sm font-mono-data text-xs" value={driveForm.url} placeholder="https://drive.google.com/..." onChange={(e) => setDriveForm({ ...driveForm, url: e.target.value })} data-testid="input-drive-url" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setDriveOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={addDriveLink} data-testid="btn-save-drive">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-1.5">
          {(driveLinks || []).length === 0 && <div className="text-xs text-slate-400">No drive links</div>}
          {(driveLinks || []).map((l, i) => (
            <a key={l.url || `drive-${i}`} href={l.url} target="_blank" rel="noreferrer"
               className="flex items-center justify-between px-3 py-2 border border-slate-200 hover:border-[#0a2540]" data-testid={`drive-link-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <ExternalLink size={14} className="text-slate-400" />
                <div className="text-xs truncate">{l.title}</div>
              </div>
              <span className="text-[10px] text-slate-400">drive</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
